// Lib
import { createSelector } from 'reselect';

// Util
import {
    getColor,
    getElementType,
    getLocationParentId,
    getPhysicalId,
    getLabels,
    isElementLocked,
} from '../../../common/elements/utils/elementPropertyUtils';
import { validateElementTypeEditPermission } from '../../../common/elements/utils/elementTypePermissionsUtils';
import { getAsyncResourcesTypeState } from '../../utils/services/http/asyncResource/asyncResourceSelector';
import { getColorCssValue } from '../utils/elementColorStyleUtils';
import {
    getCurrentlyEditingId,
    getCurrentlyEditingEditorKey,
    getCurrentlyEditingEditorId,
} from '../selection/currentlyEditingSelector';
import { getSelectedElementIds } from '../selection/selectedElementsSelector';
import { getDragModifierKeys } from '../../utils/dnd/modifierKeys/dragModifierKeysSelector';
import { getIsElectronSelector } from '../../components/electron/electronSelector';
import { getSpellCheckStatus } from '../../../common/electron/electronSelectors';
import { getIsElementOpenInModal } from '../modal/elementModalCommonSelector';

// Selectors
import { getIsElementClipboardCutSelector } from '../../workspace/shortcuts/clipboard/clipboardSelectors';
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { attachModeHoveredElementIdSelector } from '../../reducers/draggingSelector';
import { getElements } from './elementsSelector';
import { elementGraphSelector } from './elementGraphSelector';
import { isDescendantId } from '../../../common/dataStructures/graphUtils';
import getAttachment from '../attachments/getAttachment';
import {
    elementFilterDataSelector,
    getElementFilterQuerySelector,
    elementFilterEnabledSelector,
    createCurrentElementFilterDataSelector,
    getElementFilterLabelScopeIdSelector,
} from '../elementFilter/elementFilterSelector';
import {
    getPresentationModeFocusedElementId,
    isPresentationModeEnabledSelector,
} from '../../workspace/presentation/presentationSelector';
import { getElementLocalData } from '../local/elementLocalDataSelector';
import { getElementIdForSelectedLabel } from '../labels/labelSelectors';
import { selectSelectedElementChip } from '../chipsPanel/elementChipsSelectors';

// Constants
import { FILTER_STATES } from '../../../common/elements/elementConstants';
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';

export { getElements } from './elementsSelector';

export const getElementId = (state, ownProps) =>
    ownProps.elementId || (ownProps.element && (ownProps.element.get('id') || ownProps.element.get('_id')));

export const getElementIdFromProps = getElementId;
export const getPhysicalElementIdSelector = (state, ownProps) => getPhysicalId(ownProps.element);
export const getElement = (state, ownProps) => ownProps.element || getElements(state).get(ownProps.elementId);

// Resources
export const elementsAsyncResourcesSelector = getAsyncResourcesTypeState(ResourceTypes.elements);

export const isEditingSelector = () =>
    createShallowSelector(
        getElementId,
        getElement,
        elementGraphSelector,
        getCurrentlyEditingId,
        getCurrentlyEditingEditorKey,
        getCurrentlyEditingEditorId,
        (elementId, element, elementGraph, currentlyEditingId, currentEditorKey, currentEditorId) => {
            let isEditing = false;
            let isEditingChild = false;
            let _currentEditorKey = null;
            let _currentEditorId = null;

            if (currentlyEditingId) {
                isEditing = currentlyEditingId === elementId;
                _currentEditorKey = isEditing ? currentEditorKey : null;
                _currentEditorId = isEditing ? currentEditorId : null;

                if (!isEditing) {
                    isEditingChild = isDescendantId(elementGraph, elementId, currentlyEditingId);
                }
            }

            return {
                isEditing,
                isEditingChild,
                currentEditorKey: _currentEditorKey,
                currentEditorId: _currentEditorId,
            };
        },
    );

export const isElementSelectedSelector = () =>
    createSelector(getElementId, getSelectedElementIds, (elementId, selectedElementIds) =>
        selectedElementIds.includes(elementId),
    );

const isSingleSelectedSelector = () =>
    createSelector(
        getElementId,
        getSelectedElementIds,
        (elementId, selectedElementIds) => selectedElementIds.size === 1 && selectedElementIds.includes(elementId),
    );

export const selectIsLabelSelected = () =>
    createSelector(
        getElementId,
        getElementIdForSelectedLabel,
        selectSelectedElementChip,
        (elementId, selectedLabelElementId, selectedElementChip) => {
            // Only label pills should drive label-selected state; other pill types (due date,
            // assignment, reaction) own their own selection and must not flag the element as label-selected.
            const isLabelPillForElement =
                selectedElementChip?.chip.type === 'label' && elementId === selectedElementChip.elementId;

            return elementId === selectedLabelElementId || isLabelPillForElement;
        },
    );

export const selectIsElementChipSelected = () =>
    createSelector(
        getElementId,
        selectSelectedElementChip,
        (elementId, selectedElementChip) => elementId === selectedElementChip?.elementId,
    );

const remoteSelectionSelector = () =>
    createSelector(
        (state, props) => state.getIn(['remoteActivity', 'selectedElements', getElementId(state, props)]),
        (remoteSelectionData) => remoteSelectionData,
    );

const isRemotelySelectedSelector = () =>
    createSelector(
        remoteSelectionSelector(),
        isPresentationModeEnabledSelector,
        (remoteSelectionData, isPresentationModeEnabled) =>
            !isPresentationModeEnabled && remoteSelectionData && !!remoteSelectionData.get('userId'),
    );

const isLockedSelector = () => (state, ownProps) => isElementLocked(getElement(state, ownProps));

/**
 * An element "isFocusedBackgroundElement" if it's open in the focus mode,
 * but it's *not* the FocusModeElement component.
 */
const isFocusedBackgroundElementSelector = () => (state, ownProps) =>
    getElementId(state, ownProps) === getPresentationModeFocusedElementId(state) &&
    !ownProps.isFocusedForegroundElement;

const isEditableSelector = () =>
    createSelector(
        (state, props) => props.permissions,
        (state, props) => getElementType(props.element),
        validateElementTypeEditPermission,
    );

const filterStateSelector = () =>
    createSelector(
        elementFilterEnabledSelector,
        createCurrentElementFilterDataSelector(),
        (elementFilterEnabled, currentElementFilterData) => {
            if (!elementFilterEnabled) return FILTER_STATES.NONE;

            if (currentElementFilterData?.focus) return FILTER_STATES.FOCUS;
            if (currentElementFilterData?.pass) return FILTER_STATES.PASS;

            return FILTER_STATES.FAIL;
        },
    );

/**
 * Only return the filter query if we're not scoping the search to a label and the
 * filter state at least partially matches
 */
const visibleFilterQuerySelector = () =>
    createSelector(
        filterStateSelector(),
        getElementFilterQuerySelector,
        getElementFilterLabelScopeIdSelector,
        (filterState, filterQuery, labelScopeId) => {
            if (labelScopeId || !filterQuery) return null;
            if (filterState !== FILTER_STATES.PASS && filterState !== FILTER_STATES.FOCUS) return null;

            return filterQuery;
        },
    );

export const getElementColorFromState = (state, ownProps) => getColor(getElement(state, ownProps));

export const getColourHex = () => createSelector(getElementColorFromState, getColorCssValue);

const canConnectLineEdgeSelector = (state, ownProps) => {
    if (!ownProps.inList) return true;

    const hoveredElementId = attachModeHoveredElementIdSelector(state);

    if (!hoveredElementId) return false;

    const { elementId, element } = ownProps;

    return elementId === hoveredElementId || getLocationParentId(element) === hoveredElementId;
};

/**
 * If the currently long hovered element ID is the same as this element ID we're connecting inside.
 */
const isAttachingInsideSelector = (state, ownProps) => attachModeHoveredElementIdSelector(state) === ownProps.elementId;

export const getElementsByLabelId = (state, labelId) => {
    const elements = getElements(state);
    return elements.filter((element) => {
        const elementTags = getLabels(element);
        return elementTags?.includes(labelId);
    });
};

export default () =>
    createSelector(
        isElementSelectedSelector(),
        isSingleSelectedSelector(),
        remoteSelectionSelector(),
        isRemotelySelectedSelector(),
        selectIsLabelSelected(),
        selectIsElementChipSelected(),
        isEditingSelector(),
        isLockedSelector(),
        isFocusedBackgroundElementSelector(),
        getIsElementClipboardCutSelector,
        filterStateSelector(),
        isEditableSelector(),
        getDragModifierKeys,
        visibleFilterQuerySelector(),
        getIsElementOpenInModal,
        getIsElectronSelector,
        getSpellCheckStatus,
        elementFilterDataSelector,
        getAttachment,
        canConnectLineEdgeSelector,
        isAttachingInsideSelector,
        isPresentationModeEnabledSelector,
        getElementLocalData,
        getElementFilterLabelScopeIdSelector,
        (
            isSelected,
            isSingleSelected,
            remoteSelectionData,
            isRemotelySelected,
            isLabelSelected,
            isElementChipSelected,
            isEditing,
            isLocked,
            isFocusedBackgroundElement,
            isClipboardCut,
            filterState,
            isEditable,
            dragModifierKeys,
            visibleFilterQuery,
            isElementOpenInModal,
            isElectron,
            spellCheckStatus,
            elementFilterData,
            attachment,
            canConnectLineEdge,
            isAttachingInside,
            isPresentationModeEnabled,
            elementLocalData,
            labelScopeId,
        ) => ({
            ...isEditing,
            remoteSelectionData,
            isRemotelySelected,
            isSingleSelected,
            isSelected,
            isLabelSelected,
            isElementChipSelected,
            isFocusedBackgroundElement,
            isLocked,
            isClipboardCut,
            filterState,
            filterQuery: visibleFilterQuery,
            isEditable,
            dragModifierKeys,
            isElementOpenInModal,
            spellCheck: isElectron ? spellCheckStatus : true,
            elementFilterData,
            attachment,
            canConnectLineEdge,
            isAttachingInside,
            isPresentationModeEnabled,
            elementLocalData,
            labelScopeId,
        }),
    );
